import { Signal, signal } from '@angular/core';

/**
 * A progress display that only appears once waiting has lasted noticeably long.
 *
 * An operation that finishes inside the delay stays silent: a display that appears and vanishes
 * again reads as a glitch rather than as feedback. App and portal show different progress displays
 * but need exactly this timing, so only the timing lives here.
 *
 * The delay stays the caller's choice — how long "noticeable" is depends on what still has to happen
 * before the user sees something.
 */
export class DelayedIndicator {
  private readonly shown = signal(false);

  /**
   * Whether the display belongs on screen.
   *
   * A signal rather than a plain flag, so a template reading it is re-rendered when the delay
   * elapses without the component having to mark itself dirty.
   */
  public readonly visible: Signal<boolean> = this.shown.asReadonly();

  private timer: ReturnType<typeof setTimeout> | null = null;

  /**
   * @param {number} delayMs - How long the operation may run before the display appears.
   */
  constructor(private readonly delayMs: number) {}

  /** Starts the quiet window. Restarts it when one is already running. */
  public schedule(): void {
    this.clearTimer();
    this.timer = setTimeout(() => this.shown.set(true), this.delayMs);
  }

  /**
   * Ends the display, whether it ever appeared or not.
   *
   * Belongs on every exit — the error paths and an abandoned view included. A timer left running
   * would otherwise put the display up over a view that is not waiting for anything any more, and a
   * progress display is the one state a user cannot leave on their own.
   */
  public hide(): void {
    this.clearTimer();
    this.shown.set(false);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
